// ═══ 【Long Task 优化】只保留首次渲染必需的静态 import ═══
// 原先 8 个静态 import 同步执行 ~1,370ms，现拆为：
//   Phase 1（静态）: global.scss + createRoot + utils ≈ 300-400ms
//   Phase 1.5（顶层动态 import）: datadogRum — 立即异步加载+init，确保 FCP 前就绪
//   Phase 2（动态，渲染前）: i18n + react-router-dom — 并行加载
//   Phase 3（FCP 后）: trackingService + antd-mobile CSS
import "./styles/global.scss";
import { createRoot } from "react-dom/client";
import utils from "./utils";

const _hostname = window.location.hostname;

/**
 * Datadog RUM — 模块顶层立即启动加载 + init
 * 必须在 FCP 之前完成 init()，否则 SDK 无法采集 FCP/LCP 等 paint timing。
 * 用动态 import 避免同步阻塞（~100KB chunk），但在模块求值时立刻触发，
 * 与页面渲染并行；已缓存时 init() 几乎同步完成，远早于 FCP。
 */
const _rumService = `gb-${_hostname.replace(/^www\./, "").replace(/\./g, "-")}`;
const _datadogReady = import("@datadog/browser-rum").then(({ datadogRum }) => {
  datadogRum.init({
    applicationId: "de2de121-dd30-48ac-9de7-88ca50b2d3f8",
    clientToken: "pub16d7c5d7707f50b61015c880cf92b7d5",
    site: "ap1.datadoghq.com",
    service: _rumService,
    env: "production",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });
  datadogRum.setGlobalContextProperty("merchant", _hostname);
});

// antd-mobile 全局样式 — 非阻塞加载
import("antd-mobile/es/global");

function initCountryAndLanguage() {
  try {
    const countryList = window._config?.countryList || [];
    const languageList = window._config?.languageList || [];

    const findCountry = (countryId: string) =>
      countryList.find(c => c.id === countryId);
    const findLanguage = (langId: string) =>
      languageList.find(l => l.id === langId);

    let finalCountryId = "";
    const savedCountryId = utils.getObject("countryId");
    if (savedCountryId && findCountry(savedCountryId)) {
      finalCountryId = savedCountryId;
    } else if (
      window._config?.countryId &&
      findCountry(window._config.countryId)
    ) {
      finalCountryId = window._config.countryId;
    } else if (countryList.length > 0) {
      finalCountryId = countryList[0].id;
    }

    if (finalCountryId) {
      const country = findCountry(finalCountryId);
      if (country) {
        utils.setObject("countryId", country.id);
        utils.setObject("currentCountry", country);
      }
    }

    let finalLanguageId = "";
    const savedLanguage = utils.getObject("currentLanguage");
    if (savedLanguage && findLanguage(savedLanguage)) {
      finalLanguageId = savedLanguage;
    } else if (window._config?.lang && findLanguage(window._config.lang)) {
      finalLanguageId = window._config.lang;
    } else if (languageList.length > 0) {
      finalLanguageId = languageList[0].id;
    } else if (finalCountryId) {
      const country = findCountry(finalCountryId);
      if (country?.lang && findLanguage(country.lang)) {
        finalLanguageId = country.lang;
      }
    }

    if (finalLanguageId) {
      utils.setObject("currentLanguage", finalLanguageId);
    }
  } catch (error) {
    console.error("初始化国家和语言失败:", error);
  }
}

// ═══ 早期路径检测 — 落地页 / 维护页走独立轻量路径，不加载主应用 ═══
const _pathname = window.location.pathname;
const _root = createRoot(document.getElementById("root") as HTMLElement);

if (_pathname === "/repair") {
  import("./pages/Maintain").then(({ default: Maintain }) => {
    _root.render(<Maintain />);
  });
} else if (_pathname.includes("/common/store/apps/details")) {
  initCountryAndLanguage();
  // 【优化5】落地页 startup API 预请求 — 在 JS chunk 加载的同时并行发起 API
  const ch = new URLSearchParams(window.location.search).get("ch") ||
    (window as any).__ch || "";
  const startupPreload = ch
    ? fetch(`/api/home_launch/startup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ch }),
      }).then(r => r.json()).catch(() => null)
    : null;
  // 挂到 window 供 LandingPageWrapper 直接使用，避免重复请求
  (window as any).__startupPreload = startupPreload;

  Promise.all([
    import("./pages/LandingPage"),
    import("react-router-dom"),
  ]).then(([{ default: LandingPageWrapper }, { BrowserRouter }]) => {
    _root.render(
      <BrowserRouter future={{ v7_startTransition: true }}>
        <LandingPageWrapper />
      </BrowserRouter>
    );
  });
} else {
  // 【P1 优化】主应用 startup API 预请求 — 在 router chunk 加载的同时并行发起
  // /api/home/startup?lazy=true 是 GET 请求，结果挂到 window 供 StartupApi 复用
  const searchParams = new URLSearchParams(window.location.search);
  const startupUrl = `/api/home/startup?lazy=true&startTime=${Date.now()}${
    searchParams.get("p") ? `&p=${searchParams.get("p")}` : ""
  }`;
  (window as any).__mainStartupPreload = fetch(startupUrl, {
    credentials: "include",
  })
    .then(r => r.json())
    .catch(() => null);

  initMainApp(_root);
}

// ═══ 主应用初始化（仅非落地页路径执行） ═══
function initMainApp(root: ReturnType<typeof createRoot>) {
  // i18n 和 router 并行加载 — 都在渲染前需要就绪
  const i18nReady = import("@/i18n");
  const routerPrefetch = import("./router/router.tsx");

  import("antd/dist/reset.css");
  import("@/utils/antdDebug").then(m => setTimeout(m.initAntiDebug, 0));

  const urlParams = new URLSearchParams(window.location.search);
  const auuid = urlParams.get("auuid");
  const uid = urlParams.get("uid");
  if (auuid && auuid !== "null") {
    utils.setObject("__auuid", auuid);
    utils.setCookie("__auuid", auuid, 7);
  }
  if (uid) {
    utils.setObject("__uid", uid);
    utils.setCookie("__uid", uid, 7);
  }

  // 首页首次加载：in-app WebView + Android 时跳出到系统默认浏览器（不强制 Chrome）
  const envInfo = utils.getEnvInfo();
  if (envInfo.context === "InAppWebView") {
    utils.tryOpenInExternalBrowser();
  }

  let chToFetch = "";
  try {
    import("./utils/dev").then(m => m.initDevCheck());
    utils.onInit();

    import("@/utils/countryCookie").then(m =>
      m.persistCountryParamsFromUrl((key: string, value: unknown) =>
        utils.setObject(key, value)
      )
    );

    const chFromUrl = utils.getQueryString("ch", window.location);
    const chFromCache = utils.getObject("ch") || window.__ch;
    if (chFromUrl && chFromUrl.trim()) {
      utils.setObject("ch", chFromUrl.trim());
      window.__ch = chFromUrl.trim();
      chToFetch = chFromUrl.trim();
    } else if (chFromCache && String(chFromCache).trim()) {
      chToFetch = String(chFromCache).trim();
    }

    initCountryAndLanguage();

    try {
      loopCheckPwa();
    } catch (e) {
      console.error("loopCheckPwa failed:", e);
    }
  } catch {
    // 忽略错误
  }

  const schedulePostFCP = (fn: () => void) => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(fn, { timeout: 5000 });
    } else {
      setTimeout(fn, 2000);
    }
  };

  schedulePostFCP(() => {
    import("@/services/trackingService").then(({ default: trackingService }) =>
      import("@/types/tracking.types").then(({ TrackingEvent, TrackingEventType }) =>
        trackingService
          .track(TrackingEvent.PV, TrackingEventType.PV, {
            eventDetail: "init",
          })
          .catch(err => console.error("初始化PV埋点失败:", err))
      )
    );

    if (chToFetch) {
      utils.fetchLaunchAgentAndCache(chToFetch).catch(() => {});
    }

    import("@/i18n/preload").then(m =>
      m
        .recommendedPreload()
        .catch((error: unknown) => console.error("i18n preload failed:", error))
    );

    import("./utils/ads").then(m => m.AdUtils.onPageView());

    (async () => {
      try {
        await initWebPush();
      } catch (e) {
        console.error("initWebPush failed:", e);
      }
    })();
    (async () => {
      try {
        await initPwa();
      } catch (e) {
        console.error("initPwa failed:", e);
      }
    })();
  });

  try {
    utils.initConfig();
    let token = utils.getQueryString("token", window.location);
    if (!token || token === "null" || token.length < 10) {
      token = "";
    }
    const pstoken = token;
    if (token) {
      const user = { token };
      utils.setUser(user);
    }
    if (pstoken) {
      let url = window.location.href;
      url = utils.removeToken(url);
      window.location.href = url;
      return;
    }
    utils.initGlobalFontSizeToHtml();

    const inviteCode = utils.getQueryString("p", window.location);
    if (inviteCode) {
      const userToken = utils.getObject("token");
      const userInfo = utils.getUser();
      const isLoggedIn =
        (userToken && userToken !== "null" && userToken.length >= 10) ||
        (userInfo && typeof userInfo === "object" && userInfo !== null);

      if (!isLoggedIn) {
        utils.setObject("invitedCode", inviteCode);
        utils.tryOpenInExternalBrowser(window.location.href);
      }
    }
  } catch {
    // 忽略错误
  }

  try {
    if (
      window.top !== window.self &&
      window.parent.location.pathname.indexOf("/h5") === -1 &&
      window.parent.location.pathname.indexOf("/chatbet") < 0
    ) {
      window.parent.location.href = window.location.href;
      return;
    }
  } catch {
    // 忽略错误
  }

  if (window._cburl) {
    window.location.href = window._cburl;
    return;
  }

  const renderMainApp = async (router: any) => {
    // 确保 i18n 初始化完成再渲染，避免组件中 useTranslation() 报错
    const [{ RouterProvider }] = await Promise.all([
      import("react-router-dom"),
      i18nReady,
    ]);
    root.render(<RouterProvider router={router} />);
  };

  const isPwaMode = utils.updateCheckIsPwa();
  const isInApp = utils.isApp();

  if (
    isInApp ||
    isPwaMode ||
    (envInfo.context !== "InAppWebView" && envInfo.os === "iOS")
  ) {
    routerPrefetch.then(m => renderMainApp(m.router));
    return;
  }

  const skipPwaGuidePage =
    localStorage.getItem("skip_pwa_guide_page") === "true";

  if (!isPwaMode && !skipPwaGuidePage) {
    utils
      .request("/api/v1/pwa_guide_page", {}, { alert: false })
      .then(resp => {
        if (resp.data.model) {
          // trackingService 动态加载 — 不阻塞渲染
          import("@/services/trackingService").then(({ default: ts }) =>
            ts
              .trackNormalLandingPagePV(
                `template_type:${resp.data.model.appName}`
              )
              .catch(err =>
                console.error("上报NORMAL_LANDING_PAGE_PV失败:", err)
              )
          );

          Promise.all([
            import("./pages/PwaGuidePage"),
            import("react-router-dom"),
          ]).then(([{ PwaGuidePageWrapper }, { BrowserRouter }]) => {
            root.render(
              <BrowserRouter future={{ v7_startTransition: true }}>
                <PwaGuidePageWrapper
                  config={resp.data.model}
                  skipWebToPwa={true}
                />
              </BrowserRouter>
            );
          });
        } else {
          routerPrefetch.then(m => renderMainApp(m.router));
        }
      })
      .catch(() => {
        routerPrefetch.then(m => renderMainApp(m.router));
      });
    return;
  }

  routerPrefetch.then(m => renderMainApp(m.router));
}

async function initPwa() {
  document.addEventListener("DOMContentLoaded", () => {
    const manifest = document.querySelector('link[rel="manifest"]');
    const fromFb = document.referrer.includes("facebook.com");
    const firstLoad = !window.location.search.includes("reloaded");

    if (
      (!manifest ||
        !(manifest as HTMLLinkElement).href.includes("manifest.webmanifest")) &&
      fromFb &&
      firstLoad
    ) {
      if (!window.location.search.includes("reloaded")) {
        const url = new URL(window.location.href);
        url.searchParams.set("reloaded", "1");
        window.location.href = url.toString();
      }
    }
  });
  window.addEventListener("beforeinstallprompt", e => {
    e.preventDefault();
    window.deferredPrompt = e;

    import("@/services/trackingService").then(({ default: ts }) =>
      ts.trackPwaInstallPrompt().catch(err => {
        console.error("上报PWA_INSTALL_PROMPT失败:", err);
      })
    );

    try {
      const hadMark = localStorage.getItem("pwa_ever_installed");
      if (hadMark === "true") {
        localStorage.removeItem("pwa_ever_installed");
        localStorage.removeItem("pwa_installed_time");
        window.dispatchEvent(new CustomEvent("PWA_STATUS_CHANGED"));
      }
    } catch (e) {
      console.error("清除PWA安装标记失败:", e);
    }
  });

  window.addEventListener("appinstalled", () => {
    const installStartTime = (window as any).__pwaInstallStartTime;
    const now = Date.now();
    const timeDiff = installStartTime ? now - installStartTime : 0;

    if (installStartTime && timeDiff < 4000) {
      import("@/services/trackingService").then(({ default: ts }) =>
        ts.trackLandingPageClick().catch(err => {
          console.error("上报LANDING_PAGE_CLICK失败:", err);
        })
      );
      return;
    }

    try {
      localStorage.setItem("pwa_ever_installed", "true");
      localStorage.setItem("pwa_installed_time", Date.now().toString());
    } catch (e) {
      console.error("保存PWA安装标记失败:", e);
    }

    (window as any).__pwaInstallStartTime = null;
    window.deferredPrompt = null;
    import("@/services/trackingService").then(({ default: ts }) =>
      ts.trackPwaAppInstalled().catch(err => {
        console.error("上报PWA_APP_INSTALLED失败:", err);
      })
    );
    window.dispatchEvent(new CustomEvent("PWA_APP_INSTALLED"));
  });

  window.deferredPromptPrompt = function () {
    if (!window.deferredPrompt) return;

    (window as any).__pwaInstallStartTime = Date.now();

    window.deferredPrompt.prompt();
    window.deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === "accepted") {
        window.dispatchEvent(new CustomEvent("PWA_INSTALL_ACCEPTED"));
      } else {
        (window as any).__pwaInstalling = false;
        window.dispatchEvent(new CustomEvent("PWA_INSTALL_REJECTED"));
        const times = Number(utils.getObject("pwa-dismissed-times") || 0);
        utils.setObject("pwa-dismissed-times", times + 1);
      }
    });
  };
}

async function loopCheckPwa() {
  if (utils.isApp()) return;
  if (utils.updateCheckIsPwa()) {
    return;
  }
}

async function initWebPush() {
  if (!("serviceWorker" in navigator)) return;

  try {
    const existingReg = await navigator.serviceWorker.getRegistration();
    if (existingReg) {
      return existingReg;
    }

    const newReg = await navigator.serviceWorker.register("/sw.js");
    return newReg;
  } catch (err) {
    console.error("SW registration failed:", err);
  }
}
